import { useNavigate } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, Dialog, Field, Input, Textarea } from '../../../components/ui';
import { useT } from '../../../i18n';
import { useCreateTeam } from '../hooks';
import {
  emptyProjectDraft,
  newProjectInput,
  projectDraftValid,
  splitNames,
  TestTypeSelect,
  useFailToast,
  type ProjectDraft,
} from './shared';

/**
 * A new team, with its first projects if they are known. A project row that
 * lacks its name, GitLab ID or pipeline URL is left out — it can be added later.
 */
export const CreateTeamDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useT();
  const navigate = useNavigate();
  const failToast = useFailToast();
  const create = useCreateTeam();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [members, setMembers] = useState('');
  const [projects, setProjects] = useState<ProjectDraft[]>([emptyProjectDraft()]);

  const reset = () => {
    setName('');
    setDescription('');
    setMembers('');
    setProjects([emptyProjectDraft()]);
  };

  const patchProject = (index: number, patch: Partial<ProjectDraft>) =>
    setProjects((all) => all.map((p, i) => (i === index ? { ...p, ...patch } : p)));

  const submit = () =>
    create.mutate(
      {
        name: name.trim(),
        description,
        memberNames: splitNames(members),
        projects: projects.filter(projectDraftValid).map(newProjectInput),
      },
      {
        onSuccess: (team) => {
          toast.success(t('automation.project.createTeam.created', { name: team.name }));
          onOpenChange(false);
          reset();
          void navigate({ to: '/automation/teams/$teamId', params: { teamId: team.id } });
        },
        onError: failToast,
      },
    );

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('automation.project.createTeam.title')}
      size="lg"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!name.trim()}
            loading={create.isPending}
            onClick={submit}
          >
            {t('automation.project.createTeam.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t('automation.project.createTeam.name')} required>
          {({ id }) => (
            <Input
              id={id}
              value={name}
              placeholder={t('automation.project.createTeam.namePlaceholder')}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('common.description')}>
          {({ id }) => (
            <Textarea
              id={id}
              rows={2}
              value={description}
              placeholder={t('automation.project.createTeam.descriptionPlaceholder')}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('automation.project.createTeam.members')}>
          {({ id }) => (
            <Input
              id={id}
              value={members}
              placeholder={t('automation.project.createTeam.membersPlaceholder')}
              onChange={(event) => setMembers(event.target.value)}
            />
          )}
        </Field>

        <div className="flex items-center justify-between border-t border-border pt-4">
          <h3 className="text-[13px] font-medium text-ink">
            {t('automation.project.createTeam.projects')}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setProjects((all) => [...all, emptyProjectDraft()])}
          >
            <Plus className="size-3.5" />
            {t('automation.project.createTeam.addProject')}
          </Button>
        </div>

        <ul className="flex flex-col gap-3">
          {projects.map((project, index) => {
            const n = index + 1;
            return (
              <li key={index} className="rounded-lg border border-border bg-canvas p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[12px] text-ink-subtle">
                    {t('automation.project.createTeam.projectN', { n })}
                  </span>
                  {projects.length > 1 ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('automation.project.createTeam.removeProject', { n })}
                      onClick={() => setProjects((all) => all.filter((_, i) => i !== index))}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  ) : null}
                </div>
                <div className="grid gap-2 sm:grid-cols-[1fr_7rem]">
                  <Input
                    aria-label={`${t('automation.project.field.name')} · ${n}`}
                    value={project.name}
                    placeholder={t('automation.project.field.namePlaceholder')}
                    onChange={(event) => patchProject(index, { name: event.target.value })}
                  />
                  <TestTypeSelect
                    aria-label={`${t('automation.project.field.testType')} · ${n}`}
                    value={project.testType}
                    onChange={(testType) => patchProject(index, { testType })}
                  />
                </div>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <Input
                    aria-label={`${t('automation.project.field.gitlabProjectId')} · ${n}`}
                    value={project.gitlabProjectId}
                    placeholder={t('automation.project.field.gitlabProjectId')}
                    onChange={(event) =>
                      patchProject(index, { gitlabProjectId: event.target.value })
                    }
                  />
                  <Input
                    aria-label={`${t('automation.project.field.pipelineUrl')} · ${n}`}
                    value={project.gitlabPipelineUrl}
                    placeholder={t('automation.project.field.pipelineUrl')}
                    onChange={(event) =>
                      patchProject(index, { gitlabPipelineUrl: event.target.value })
                    }
                  />
                </div>
              </li>
            );
          })}
        </ul>
        <p className="text-[12px] text-ink-subtle">
          {t('automation.project.createTeam.projectsHint')}
        </p>
      </div>
    </Dialog>
  );
};
